import '@/styles/index.scss';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import favicon from '../public/favicon.ico';
import '@fabric-app/compat/dayjs';

// Fix issue in older android versions
// To note this is already added on next >=14, but not on next 13
import 'core-js/features/object/has-own';
import type { NextPageWithAuth } from '../src/types/page';
import { useInitializeTracker } from '../src/lib/or';
import { useFabricMobileSheet } from '../src/modules/mobile/hooks/useFabricMobileSheet';
import { ErrorBoundary } from '@sentry/nextjs';
import FabricApp from '../src/components/FabricApp';
import { WoodyContextProvider } from '@fabric-app/business/woody/woody';
import { woodyLogLevel } from '@/src/services/woody/woody';
import { setupCompat } from '../src/lib/setupCompat';
import { QueryClientProvider } from '../src/lib/react-query/ReactQueryClientProvider';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { DefaultSeo } from 'next-seo';
import { DefaultSEO } from '@/src/lib/seo';

interface Props extends AppProps {
  pageProps: {
    origin: string | undefined;
    referer: string | undefined;

    title: string;
    image: string;
    description: string;
    url: string;
    bigCard: boolean;
  };

  Component: NextPageWithAuth;
}

setupCompat();
export default function NextApp({ Component, pageProps, router, __N_SSG, __N_SSP }: Props) {
  useRunOnce(
    () => {
      setupCompat();
    },
    {
      useEffectFn: useClientLayoutEffect,
    },
  );

  useInitializeTracker();
  useFabricMobileSheet();

  return (
    <>
      <DefaultSeo {...DefaultSEO} />
      <Head>
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="viewport"
          content="viewport-fit=cover, width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=0"
        />
        <link rel="icon" href={favicon.src} />
      </Head>
      <ErrorBoundary>
        <QueryClientProvider>
          <WoodyContextProvider logLevel={woodyLogLevel}>
            <FabricApp
              Component={Component}
              pageProps={pageProps}
              router={router}
              __N_SSG={__N_SSG}
              __N_SSP={__N_SSP}
            />
          </WoodyContextProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </>
  );
}
